/**
 * ada-accessibility.js
 *
 * Additive accessibility layer for the Magnolia Boutique theme.
 *
 * This file exists so that accessibility behaviour can be added WITHOUT editing
 * assets/global.js, which is minified in this theme and holds Dawn's focus-trap
 * and modal machinery. Nothing here modifies or replaces existing theme
 * behaviour - it only adds ARIA state that the theme never set.
 *
 * ---------------------------------------------------------------------------
 * ADA fix (2a-7): expose the header search toggle's expanded/collapsed state.
 *
 * Problem this solves:
 *   Dawn's global.js auto-manages aria-expanded and aria-controls, but only for
 *   `<details>` elements whose id begins with "Details-":
 *       document.querySelectorAll('[id^="Details-"] summary')
 *   The search modal in snippets/header-search.liquid has NO id on its
 *   `<details>`, so global.js skips it entirely. assets/details-modal.js sets
 *   only role="button" on the summary and never sets aria-expanded or
 *   aria-controls. The result is that the search button never told a screen
 *   reader whether the search panel was open or closed - one of the barriers
 *   named in the ADA complaint ("the search and cart functions are not
 *   navigable").
 *
 * Why a MutationObserver rather than a click listener:
 *   The `open` attribute on the `<details>` is changed through five different
 *   paths - summary click, the close button, the Escape key, an outside click,
 *   and DetailsModal.close() called programmatically. DetailsModal also calls
 *   event.preventDefault() on the summary click and then sets `open` itself, so
 *   a click listener would race it and could report the wrong state. Observing
 *   the attribute reflects the real state no matter what changed it, and cannot
 *   desync.
 *
 * Why aria-expanded is set here and not in the Liquid markup:
 *   A static attribute would be correct only until the first interaction, and a
 *   stale aria-expanded is worse than an absent one. It is also correct that no
 *   value exists when JavaScript is unavailable: the native <details>/<summary>
 *   disclosure semantics apply in that case and convey the state on their own.
 *
 * Scope: the header search toggle ONLY. The selector below is deliberately
 * narrow. `details-modal.header__search` is the only <details-modal> in the
 * theme and the class `header__search` appears nowhere else, so this cannot
 * match any other <details> element. Do not widen it - other disclosures are
 * either handled by global.js or need their own reviewed fix.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /** Matches only the search modal's summary. See scope note above. */
  var SEARCH_TOGGLE_SELECTOR = 'details-modal.header__search > details > summary';

  /** Marks a toggle as already wired so re-initialisation is a no-op. */
  var WIRED_FLAG = 'adaSearchToggleWired';

  var panelIdCounter = 0;

  function syncExpandedState(summary, details) {
    summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
  }

  function wireSearchToggle(summary) {
    if (!summary || summary.dataset[WIRED_FLAG] === 'true') return;

    var details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return;

    // The dialog panel is the sibling of the summary inside the same <details>.
    var panel = details.querySelector('.search-modal[role="dialog"]');
    if (!panel) return;

    // aria-controls needs a target id, and the theme's markup does not set one.
    if (!panel.id) {
      panelIdCounter += 1;
      panel.id = 'ADA-SearchModal-' + panelIdCounter;
    }
    summary.setAttribute('aria-controls', panel.id);

    syncExpandedState(summary, details);

    if (typeof MutationObserver === 'function') {
      new MutationObserver(function () {
        syncExpandedState(summary, details);
      }).observe(details, { attributes: true, attributeFilter: ['open'] });
    }

    summary.dataset[WIRED_FLAG] = 'true';
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SEARCH_TOGGLE_SELECTOR).forEach(wireSearchToggle);
  }

  init(document);

  // The header section can be re-rendered in the Shopify theme editor, which
  // replaces the markup and drops the wiring. Re-apply it when that happens.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();

/**
 * ---------------------------------------------------------------------------
 * ADA: desktop main-menu keyboard auto-open.
 *
 * When a top-level mega-menu <summary> receives focus BY KEYBOARD, its panel is
 * expanded so keyboard and screen-reader users reach the submenu links without
 * having to guess that the item is expandable.
 *
 * HISTORY -- read this before editing. An earlier version of this block was
 * removed in ca5f3b9 because it carried three defects, all measured. It is
 * reinstated here with all three fixed. Do not reintroduce them:
 *
 *   1. POINTER MUST BE UNAFFECTED. The auto-open runs only when focus arrived
 *      via Tab. viaKeyboard starts false and is reset to false on mousedown and
 *      touchstart in the CAPTURE phase, so a click or tap disarms it before the
 *      focus event is dispatched. Pointer users keep the plain native toggle.
 *
 *   2. ENTER / SPACE MUST NOT IMMEDIATELY CLOSE THE PANEL. <summary> activation
 *      TOGGLES its <details>. Because focus has already opened the panel, the
 *      native toggle would flip it straight back shut -- that was the old bug.
 *      We suppress the native activation exactly ONCE, on the first Enter/Space
 *      after a focus-open, then hand control back: a second Enter/Space closes
 *      the panel deliberately, and everything after that is plain native
 *      behaviour. The one-shot is tracked per <details> in
 *      dataset.adaOpenedByFocus and cleared whenever the panel closes.
 *
 *   3. ESCAPE MUST NOT CAUSE A REOPEN. Dawn's onKeyUpEscape (assets/global.js)
 *      closes the <details> on KEYUP and then calls summary.focus(). That
 *      refocus would re-enter our focus handler and reopen the panel -- the
 *      second old bug. We listen for Escape on KEYDOWN, which fires first, and
 *      arm a one-shot suppression that the subsequent refocus consumes.
 *
 * Only one panel is open at a time: opening one closes any other via the
 * <header-menu> element's own close(), so aria-expanded stays consistent.
 *
 * Scope: '.header__inline-menu header-menu > details' -- the desktop inline
 * menu only, which base.css renders only at min-width 990px. The mobile drawer
 * is a different component and is untouched.
 *
 * KNOWN AND ACCEPTED, decided twice: with this block active, Tab from a
 * top-level item moves INTO that item's panel and through its links before
 * reaching the next item and then the search toggle. That is deliberate. It is
 * the reason ca5f3b9 removed the earlier version, it was reinstated on explicit
 * instruction, and when d744878 tried to take those links back out of the Tab
 * sequence with a roving tabindex that was rejected as well: Tab must reach the
 * mega-menu content naturally.
 *
 * So do NOT "fix" the fact that submenu links precede Search in the Tab order.
 * It follows directly from opening the panel on focus and is the requested
 * behaviour. What that leaves -- pressing Enter one stop early activates
 * whichever link is focused -- is inherent to any tabbable menu, not a defect
 * in this block. Fix 4 below addresses the part that WAS a defect: the panel
 * staying open after focus had left the menu.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var MENU_SCOPE = '.header__inline-menu header-menu > details';
  var MENU_SUMMARY = MENU_SCOPE + ' > summary';
  var WIRED_FLAG = 'adaMenuFocusWired';
  var OPENED_BY_FOCUS = 'adaOpenedByFocus';

  // Default false so a pointer interaction is never treated as keyboard
  // navigation before any key has been pressed.
  var viaKeyboard = false;
  var suppressNextFocusOpen = false;

  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Tab') return;
      viaKeyboard = true;
      // Clear any armed Escape suppression. It may still be set: when Escape is
      // pressed while focus is already ON the summary, Dawn's summary.focus() is
      // a no-op, no focus event fires, and the one-shot is never consumed. Left
      // armed it would swallow the NEXT summary's auto-open. Tab always precedes
      // the focus move, so clearing here is both safe and sufficient.
      suppressNextFocusOpen = false;
    },
    true
  );

  function markPointer() {
    viaKeyboard = false;
  }
  document.addEventListener('mousedown', markPointer, true);
  document.addEventListener('touchstart', markPointer, { capture: true, passive: true });

  // Fix 3: runs before Dawn's keyup Escape handler, which closes the panel and
  // then refocuses the summary. The refocus consumes this one-shot.
  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      var target = event.target;
      if (target && target.closest && target.closest(MENU_SCOPE)) {
        suppressNextFocusOpen = true;
      }
    },
    true
  );

  function closeMenu(details) {
    var host = details.parentElement; // the <header-menu> custom element
    if (host && typeof host.close === 'function') {
      host.close(); // the theme's own close(), which also resets aria-expanded
      return;
    }
    details.removeAttribute('open');
    var summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  function closeOtherMenus(keepOpen) {
    document.querySelectorAll(MENU_SCOPE + '[open]').forEach(function (details) {
      if (details !== keepOpen) closeMenu(details);
    });
  }

  /* ---------------------------------------------------------------------
   * Fix 4 -- an auto-opened panel must close when focus leaves the menu.
   *
   * HISTORY, read before changing this. d744878 tried to solve the reported
   * "tabbing to Search goes to Fall Collection" by holding a focus-opened
   * panel's links OUT of the Tab sequence with a roving tabindex. That was
   * rejected: the required behaviour is that Tab moves from the trigger into
   * the opened panel and through its links naturally. The roving tabindex,
   * the Arrow/Home/End handlers and the "Enter enters the panel" behaviour
   * that came with it are all reverted -- the focus handler and Fixes 1-3
   * above are byte-identical to what they were before d744878, and the panel
   * links carry no tabindex of any kind.
   *
   * WHAT IS LEFT TO FIX, and this part is a genuine defect. The panel never
   * closed once focus moved past it: measured on a reproduction of this
   * header, Details-HeaderMenu-2 was still `open` with aria-expanded="true"
   * while focus sat on Search, then Log in, then Cart, then the hero banner
   * link. So a keyboard user arriving at Search had a full mega-menu panel
   * still displayed over the page and a menu toggle still claiming to be
   * expanded -- which is exactly the state in which someone presses Enter
   * believing they are somewhere they are not.
   *
   * Closing on focusout fixes that without touching the Tab order: the panel
   * shuts the moment focus reaches Search, so leaving the menu is visible and
   * aria-expanded stops lying. Every link inside stays reachable by Tab for
   * as long as focus is inside the menu.
   *
   * relatedTarget is the element focus is moving TO. It is null when focus
   * leaves the document entirely (window blur, devtools), and closing then
   * would shut the menu behind the user's back, so that case is left alone.
   * A pointer-opened menu is left alone too: those are governed by the
   * theme's own hover/click behaviour in custom.js, and closing them on
   * focusout would fight it.
   * ------------------------------------------------------------------- */
  function closeIfFocusLeft(details, nextFocused) {
    if (!details.hasAttribute('open')) return;
    if (details.dataset[OPENED_BY_FOCUS] !== 'true') return; // pointer-opened: not ours
    if (!nextFocused) return; // focus left the document entirely
    if (details.contains(nextFocused)) return; // still inside this menu
    closeMenu(details);
  }

  function wireSummary(summary) {
    if (!summary || summary.dataset[WIRED_FLAG] === 'true') return;

    var details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return;

    summary.addEventListener('focus', function () {
      // Fix 3: this focus is Dawn returning focus after Escape. Swallow it once.
      if (suppressNextFocusOpen) {
        suppressNextFocusOpen = false;
        return;
      }
      if (!viaKeyboard) return; // Fix 1: pointer users keep the native toggle
      closeOtherMenus(details);
      if (!details.hasAttribute('open')) {
        details.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
        details.dataset[OPENED_BY_FOCUS] = 'true';
      }
    });

    // Fix 2: suppress the native <summary> activation once, so the panel our
    // focus handler just opened is not toggled straight back shut. Clearing the
    // flag here means the NEXT Enter/Space closes it normally.
    summary.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      if (details.dataset[OPENED_BY_FOCUS] !== 'true') return;
      event.preventDefault();
      delete details.dataset[OPENED_BY_FOCUS];
    });

    // Fix 4: leaving the menu closes the panel this focus handler opened.
    details.addEventListener('focusout', function (event) {
      closeIfFocusLeft(details, event.relatedTarget);
    });

    // Any close -- Escape, a pointer toggle, or another menu opening -- ends the
    // one-shot, so the flag can never leak into a later interaction.
    details.addEventListener('toggle', function () {
      if (!details.hasAttribute('open')) delete details.dataset[OPENED_BY_FOCUS];
    });

    summary.dataset[WIRED_FLAG] = 'true';
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(MENU_SUMMARY).forEach(wireSummary);
  }

  init(document);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();

/**
 * ---------------------------------------------------------------------------
 * ADA fix: announcement bar pause/play (WCAG 2.2.2 Pause, Stop, Hide, Level A).
 *
 * The announcement bar auto-rotates with no user control. The button itself is
 * rendered in sections/announcement-bar.liquid with Dawn's own
 * `.slideshow__autoplay` class, which means Dawn's SlideshowComponent picks it
 * up and binds autoPlayToggle() for free -- rotation, the paused class and the
 * aria-label swap are all Dawn's tested code. No rotation logic is duplicated
 * here.
 *
 * This block adds only the three things Dawn does not do:
 *
 *   1. prefers-reduced-motion. Dawn's setAutoPlay() honours reduced motion ONLY
 *      in the branch where no autoplay button exists:
 *          querySelector('.slideshow__autoplay')
 *            ? (bind, autoplayButtonIsSetToPlay = true, play())
 *            : (reducedMotion.matches || arrowClicked) ? pause() : play()
 *      So adding the button would, on its own, have STARTED autoplay for users
 *      who asked for reduced motion -- a regression. We re-assert the correct
 *      state after Dawn initialises, and also react if the setting changes
 *      mid-session.
 *
 *   2. sessionStorage persistence, so a pause survives page navigation and is
 *      not silently undone on the next page.
 *
 *   3. a visually-hidden text label, kept in sync with Dawn's class changes via
 *      MutationObserver (Dawn only swaps aria-label). No aria-pressed: the name
 *      carries the state, and doing both contradicts itself.
 *
 * State is changed by calling Dawn's own autoPlayToggle() rather than by
 * dispatching a synthetic click, so the class, the aria-label and the internal
 * autoplayButtonIsSetToPlay flag stay consistent and our own click handler does
 * not fire and persist a preference the user never chose.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var TOGGLE_SELECTOR = '[data-ada-autoplay-toggle]';
  var PAUSED_CLASS = 'slideshow__autoplay--paused';
  var STORE_KEY = 'ada:announcement-autoplay-paused';
  var WIRED_FLAG = 'adaAutoplayWired';

  function readPreference() {
    try {
      return sessionStorage.getItem(STORE_KEY);
    } catch (error) {
      return null; // Safari private mode and blocked storage both throw here.
    }
  }

  function writePreference(value) {
    try {
      sessionStorage.setItem(STORE_KEY, value);
    } catch (error) {
      /* storage unavailable: the control still works, it just will not persist */
    }
  }

  function isPaused(button) {
    return button.classList.contains(PAUSED_CLASS);
  }

  // Mirror Dawn's visual state into the attributes assistive tech relies on.
  function syncState(button) {
    var paused = isPaused(button);
    var label = paused
      ? button.dataset.adaPlayLabel || 'Play announcements'
      : button.dataset.adaPauseLabel || 'Pause announcements';

    // ADA fix: ONE state model. The accessible name carries the state ("Pause
    // announcements" / "Play announcements"), so aria-pressed is not set -- a toggle that
    // both renames itself AND exposes a pressed state is announced twice and contradicts
    // itself ("Play announcements, not pressed"). The attribute is also removed from the
    // markup in sections/announcement-bar.liquid.
    button.setAttribute('aria-label', label);

    var hiddenText = button.querySelector('[data-ada-autoplay-text]');
    if (hiddenText) hiddenText.textContent = label;
  }

  function pauseIfPlaying(component) {
    if (component.autoplayButtonIsSetToPlay === true && typeof component.autoPlayToggle === 'function') {
      component.autoPlayToggle();
      return true;
    }
    return false;
  }

  function wire(button) {
    if (!button || button.dataset[WIRED_FLAG] === 'true') return;

    var component = button.closest('slideshow-component');
    if (!component) return;

    // Dawn flips PAUSED_CLASS in togglePlayButtonState(); follow it rather than
    // trying to predict it, so the two never disagree.
    if (typeof MutationObserver === 'function') {
      new MutationObserver(function () {
        syncState(button);
      }).observe(button, { attributes: true, attributeFilter: ['class'] });
    }

    button.addEventListener('click', function () {
      // Dawn's handler runs on this same event; read the resulting state after it.
      window.setTimeout(function () {
        writePreference(isPaused(button) ? '1' : '0');
      }, 0);
    });

    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (prefersReduced.matches || readPreference() === '1') {
      pauseIfPlaying(component);
    }

    if (typeof prefersReduced.addEventListener === 'function') {
      prefersReduced.addEventListener('change', function (event) {
        if (event.matches) pauseIfPlaying(component);
      });
    }

    syncState(button);
    button.dataset[WIRED_FLAG] = 'true';
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(TOGGLE_SELECTOR).forEach(wire);
  }

  init(document);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();

/**
 * ---------------------------------------------------------------------------
 * ADA fix: keyboard-operable tabs (WCAG 2.1.1 Keyboard, Level A).
 *
 * The sitemap tab switcher was four visually-hidden radio inputs, each followed
 * by a <label role="tab">. A <label> is not focusable, so Tab never reached the
 * tabs and arrow keys did nothing -- the switcher was mouse-only. The radios are
 * gone; sections/sitemap.liquid now renders native <button role="tab"> elements
 * and this controller drives them.
 *
 * Implements the WAI-ARIA tabs pattern with a roving tabindex:
 *   Tab              reaches the ACTIVE tab only, then moves into the panel
 *   ArrowLeft/Right  move between tabs, wrapping at both ends
 *   Home / End       jump to the first / last tab
 *   Enter / Space     native <button> activation, no extra handling needed
 *
 * Activation is manual, not automatic-on-focus: arrow keys move focus and
 * select, which is the APG "tabs with automatic activation" variant and is
 * appropriate here because switching panels is cheap and has no side effects.
 * Focus is NOT moved into the panel on activation -- the previous code did that,
 * which fights arrow-key navigation. The panels contain links, so they need no
 * tabindex of their own to be reachable.
 *
 * Written generically against [role="tablist"] so it covers the sitemap section
 * and any future tablist, and scoped per-tablist so several can coexist.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var WIRED_FLAG = 'adaTabsWired';

  function tabsOf(tablist) {
    return Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));
  }

  function panelFor(tab) {
    var id = tab.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function select(tablist, tab, moveFocus) {
    tabsOf(tablist).forEach(function (candidate) {
      var isTarget = candidate === tab;
      candidate.setAttribute('aria-selected', isTarget ? 'true' : 'false');
      // Roving tabindex: only the selected tab is in the tab order.
      candidate.setAttribute('tabindex', isTarget ? '0' : '-1');

      var panel = panelFor(candidate);
      if (panel) panel.classList.toggle('active', isTarget);
    });

    if (moveFocus) tab.focus();
  }

  function wire(tablist) {
    if (tablist.dataset[WIRED_FLAG] === 'true') return;

    var tabs = tabsOf(tablist);
    if (!tabs.length) return;

    tablist.addEventListener('click', function (event) {
      var tab = event.target.closest('[role="tab"]');
      if (tab && tablist.contains(tab)) select(tablist, tab, false);
    });

    tablist.addEventListener('keydown', function (event) {
      var current = event.target.closest('[role="tab"]');
      if (!current || !tablist.contains(current)) return;

      var list = tabsOf(tablist);
      var index = list.indexOf(current);
      var next = null;

      switch (event.key) {
        case 'ArrowRight':
          next = list[(index + 1) % list.length];
          break;
        case 'ArrowLeft':
          next = list[(index - 1 + list.length) % list.length];
          break;
        case 'Home':
          next = list[0];
          break;
        case 'End':
          next = list[list.length - 1];
          break;
        default:
          return; // leave Enter, Space and Tab to the browser
      }

      event.preventDefault();
      select(tablist, next, true);
    });

    // Adopt whichever tab the markup marked selected, so the server-rendered
    // state and the panel classes cannot disagree.
    var initial = list0(tabs);
    select(tablist, initial, false);

    tablist.dataset[WIRED_FLAG] = 'true';
  }

  function list0(tabs) {
    for (var i = 0; i < tabs.length; i++) {
      if (tabs[i].getAttribute('aria-selected') === 'true') return tabs[i];
    }
    return tabs[0];
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[role="tablist"]').forEach(wire);
  }

  init(document);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();

/* ===========================================================================
 * Block 5 -- Add-to-cart screen-reader announcement (WCAG 4.1.3 Status Messages)
 *
 * ADA fix F12/F13/F14.
 *
 * WHAT WAS WRONG. Adding a product to the cart announced nothing:
 *   F12  assets/cart-drawer.js renderContents() -- the add-to-cart path -- swaps the
 *        drawer HTML and opens it, and writes no status text at all. assets/product-form.js
 *        calls it directly on success and likewise announces nothing.
 *   F13  snippets/cart-drawer.liquid ships <p id="CartDrawer-LiveRegionText" role="status">
 *        EMPTY, and nothing ever fills it. Its only writer, cart.js updateLiveRegions(),
 *        runs solely on quantity change / remove and sets text only on error.
 *   F14  Even a filled one could not announce: renderContents() replaces the innerHTML of
 *        #CartDrawer, which contains that element, so the live region is destroyed and
 *        recreated. Screen readers announce changes WITHIN an existing live region, not
 *        text that is present when a region is inserted.
 *
 * HOW THIS FIXES IT. The region (#AdaCartLiveRegion, layout/theme.liquid) sits outside
 * <cart-drawer>, after </main> and the footer sections, so no section-render target is an
 * ancestor of it and it survives every re-render. This block subscribes to the theme's own
 * cartUpdate pub/sub event and writes one sentence into it.
 *
 * WHY PUB/SUB RATHER THAN EDITING THE CART FILES. product-form.js and quick-add-variants.js
 * both already publish PUB_SUB_EVENTS.cartUpdate with source 'product-form' on a successful
 * add. Subscribing is purely additive: assets/cart.js, assets/cart-drawer.js,
 * assets/product-form.js and assets/quick-add-variants.js are not modified, so the cart,
 * drawer and add-to-cart behaviour are untouched.
 *
 * WHY THE PRICE IS READ FROM THE DOM. The /cart/add.js response gives prices in cents only
 * and this theme loads no money formatter (no Shopify.formatMoney), so formatting them here
 * would mean guessing the shop's money_format. The freshly rendered drawer already contains
 * the correctly formatted price and cart total, so those are read from it. If a value cannot
 * be resolved the sentence simply omits it -- raw cents are never announced.
 *
 * Wording is built only from translation keys that already exist in the locale files
 * (general.cart.item_added, sections.cart.headings.quantity, sections.cart.estimated_total),
 * exposed as window.adaCartStrings. No locale file is modified.
 *
 * ---------------------------------------------------------------------------------------
 * LATER FIX -- the announcement did not say WHICH VARIANT had been added, and quoted the
 * wrong price.
 *
 * NVDA testing found that adding a product with variants announced the product and a figure
 * but no size or colour, so a blind shopper could not tell what they had actually bought.
 * Two causes, both in how this block read the rendered drawer:
 *
 *   1. It took the product name from `.cart-item__name`, which snippets/cart-drawer.liquid
 *      renders as `{{ item.product.title }}` and NOTHING ELSE. The selected variant is a
 *      separate <dl> of <div class="product-option"><dt>Size:</dt><dd>Medium</dd> pairs in
 *      `.cart-item__details`, which nothing was reading. rowVariant() now reads it.
 *
 *   2. It took the price from the drawer's "Total" column, which is item.original_line_price
 *      -- quantity x price. That is not the price of the thing just added as soon as the
 *      quantity is above one. rowVariantPrice() now reads the SELECTED VARIANT's own price
 *      from the details cell (item.final_price / item.original_price), falling back to the
 *      Total column, which is the same figure when the quantity is one.
 *
 * The sentence is ordered product -> variant -> quantity -> price, then the confirmation and
 * the cart total. Every clause is omitted when the drawer does not render it, so a product
 * with no variants reads exactly as it did before.
 * ======================================================================== */
(function () {
  'use strict';

  var REGION_ID = 'AdaCartLiveRegion';
  var POLL_INTERVAL_MS = 50;
  var POLL_TIMEOUT_MS = 3000;

  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  function strings() {
    return window.adaCartStrings || {};
  }

  function text(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  /* The add response is a single line item, or { items: [...] } for a multi-add. */
  function firstItem(data) {
    if (!data) return null;
    if (data.items && data.items.length) return data.items[0];
    return data;
  }

  function drawerRowFor(variantId) {
    if (!variantId) return null;
    var input = document.querySelector(
      '#CartDrawer [data-quantity-variant-id="' + String(variantId).replace(/"/g, '') + '"]'
    );
    return input ? input.closest('.cart-item') : null;
  }

  /* The line total, from the drawer's "Total" column. In the discounted case the wrapper
   * holds an <s> with the original price followed by the current one, so <s> is excluded and
   * the last remaining .price is taken. */
  function rowLineTotal(row) {
    if (!row) return '';
    var prices = row.querySelectorAll('.cart-item__price-wrapper .price');
    var last = '';
    for (var i = 0; i < prices.length; i++) {
      if (prices[i].tagName === 'S') continue;
      if (prices[i].closest('s')) continue;
      last = text(prices[i]);
    }
    return last;
  }

  /* The SELECTED VARIANT's own price, which the drawer renders in the product-details cell
   * (snippets/cart-drawer.liquid: item.original_price / item.final_price). This is the figure
   * that answers "what does the thing I just added cost", independently of quantity -- the
   * Total column holds quantity x price, which is a different number as soon as qty > 1.
   * Discounted lines expose it as .cart-item__final-price; undiscounted lines as a
   * .product-option that is a DIRECT child of .cart-item__details (the option name/value
   * wrappers also carry .product-option, but they live inside the <dl>). */
  function rowVariantPrice(row) {
    if (!row) return '';
    var details = row.querySelector('.cart-item__details');
    if (!details) return '';
    var discounted = details.querySelector('.cart-item__final-price');
    if (discounted) return text(discounted);
    for (var i = 0; i < details.children.length; i++) {
      var child = details.children[i];
      if (child.classList && child.classList.contains('product-option')) return text(child);
    }
    return '';
  }

  /* The selected variant, e.g. "Size: Medium, Color: Ivory".
   *
   * .cart-item__name is ONLY item.product.title -- the variant never appears in it. The
   * options are a separate <dl> of <div class="product-option"><dt>Size:</dt><dd>Medium</dd>
   * in .cart-item__details, which nothing was reading, so a product with variants announced
   * with no indication of WHICH variant had been added. Custom line-item properties render
   * in the same <dl> and are included too: they are part of what the shopper configured.
   *
   * Scoped to `dl` so the sibling price .product-option cannot be picked up as an option. */
  function rowVariant(row) {
    if (!row) return '';
    var details = row.querySelector('.cart-item__details');
    if (!details) return '';
    var seen = {};
    var out = [];
    var pairs = details.querySelectorAll('dl .product-option');
    for (var i = 0; i < pairs.length; i++) {
      var dt = text(pairs[i].querySelector('dt')).replace(/:$/, '');
      var dd = text(pairs[i].querySelector('dd')).replace(/,$/, '').trim();
      if (!dd) continue;
      var line = dt ? dt + ': ' + dd : dd;
      if (seen[line]) continue;
      seen[line] = true;
      out.push(line);
    }
    return out.join(', ');
  }

  function rowQuantity(row) {
    if (!row) return '';
    var input = row.querySelector('.quantity__input');
    return input && input.value ? String(input.value) : '';
  }

  function cartTotal() {
    return text(document.querySelector('#CartDrawer .totals__total-value'));
  }

  /* Order is deliberate: product -> selected variant -> quantity -> price, so the shopper
   * hears WHAT was added and WHICH one before any numbers, and the price lands last where it
   * is easiest to hold on to. Every part is optional and simply omitted when the drawer does
   * not render it (a product with no variants contributes no variant clause), so the sentence
   * stays grammatical for every product shape. */
  function compose(row, item) {
    var s = strings();
    var parts = [];

    var title = text(row && row.querySelector('.cart-item__name')) || (item && item.product_title) || '';
    if (!title) return '';
    parts.push(title);

    // Fall back to the response's variant_title ("Medium / Ivory") when the drawer row cannot
    // be read; it carries no option NAMES, so the rendered <dl> is preferred when available.
    var variant = rowVariant(row);
    if (!variant && item && item.variant_title && item.variant_title !== 'Default Title') {
      variant = String(item.variant_title).split(' / ').join(', ');
    }
    if (variant) parts.push(variant);

    // Line quantity as the cart now holds it -- this is what the drawer displays, and for a
    // fresh add it equals the quantity added. Falls back to the quantity in the response.
    var qty = rowQuantity(row) || (item && item.quantity ? String(item.quantity) : '');
    if (qty) parts.push((s.quantity || 'Quantity') + ' ' + qty);

    // The selected variant's own price, not quantity x price. Falls back to the Total column
    // if the details cell is unreadable, which is the same figure whenever quantity is 1.
    var price = rowVariantPrice(row) || rowLineTotal(row);
    if (price) parts.push(price);

    var sentence = parts.join(', ');
    if (s.itemAdded) sentence += '. ' + s.itemAdded;
    sentence += '.';

    var total = cartTotal();
    if (total) sentence += ' ' + (s.estimatedTotal || 'Estimated total') + ' ' + total + '.';

    return sentence;
  }

  /* Clearing first, then writing on a later tick, guarantees a content CHANGE inside the
   * region even when the identical product is added twice in a row -- writing the same
   * string over itself is a no-op and would not be announced. */
  /* Laid out at all? A closed <details> gives its contents no boxes, which is what rules the
   * search modal out while it is shut. Only `display: none` is treated as disqualifying on an
   * ancestor -- deliberately NOT `visibility: hidden`, because the cart drawer is hidden that
   * way at rest and the class that reveals it varies between themes; using visibility here
   * made the check miss the very dialog it exists to find. */
  function isLaidOut(el) {
    for (var n = el; n && n.nodeType === 1; n = n.parentElement) {
      if (window.getComputedStyle(n).display === 'none') return false;
    }
    return el.getClientRects().length > 0;
  }

  /* The live region node, re-homed so it is somewhere a screen reader will actually read.
   *
   * THIS IS THE FIX FOR "the drawer shows everything but NVDA announces nothing".
   *
   * F14 moved this region OUTSIDE #CartDrawer so it would survive renderContents() replacing
   * that container's innerHTML. Correct for survival -- and fatal for announcement, because
   * snippets/cart-drawer.liquid gives .drawer__inner `aria-modal="true"`. aria-modal tells
   * assistive technology to treat everything OUTSIDE the dialog as hidden, so while the drawer
   * is open -- exactly when this fires -- the region was outside the only subtree NVDA was
   * looking at. Nothing was spoken. The Featured Collection price works because no modal is
   * involved there; that is why one worked and the other did not.
   *
   * Two requirements that pull in opposite directions: survive the innerHTML swap (be outside)
   * and be announced under aria-modal (be inside). Both are met by keeping ONE persistent node
   * and moving it into the open dialog at announce time, after the re-render has happened. It
   * is the same element object throughout, never a freshly inserted one, which is what screen
   * readers require of a live region. When no modal is open it lives on <body> as before, so
   * non-modal announcements are unchanged.
   */
  var regionNode = null;

  function liveRegion() {
    if (!regionNode || !regionNode.isConnected) {
      regionNode = document.getElementById(REGION_ID) || regionNode;
    }
    if (!regionNode) return null;

    // If a previous render detached it, put it back before doing anything else.
    if (!regionNode.isConnected) document.body.appendChild(regionNode);

    /* Which modal is the user actually in?
     *
     * Primary signal is FOCUS. Dawn's CartDrawer.open() ends in
     * trapFocus(container, '.drawer__inner'), so when the drawer is open the focus is inside
     * it. That is both the most reliable indicator that a dialog is the active one and the
     * exact definition of the subtree a screen reader is confined to under aria-modal.
     * Layout is only the fallback, for the moment between render and focus landing. */
    var host = document.body;
    var dialogs = document.querySelectorAll('[aria-modal="true"]');
    var active = document.activeElement;
    var i;

    for (i = 0; i < dialogs.length; i++) {
      if (active && dialogs[i].contains(active)) { host = dialogs[i]; break; }
    }
    if (host === document.body) {
      for (i = 0; i < dialogs.length; i++) {
        if (isLaidOut(dialogs[i])) { host = dialogs[i]; break; }
      }
    }

    if (regionNode.parentElement !== host) host.appendChild(regionNode);
    return regionNode;
  }

  /* Every announcement in this block goes through here. lastAnnounceAt is the handle the
   * total-only paths below use to stand down when a richer sentence has just been spoken,
   * so adding an item never produces both "... added to your cart. Estimated total $X" and a
   * bare "Estimated total, $X" one after the other. */
  var lastAnnounceAt = 0;

  function announce(message) {
    var region = liveRegion();
    if (!region || !message) return;
    lastAnnounceAt = Date.now();
    region.textContent = '';
    // The delay both guarantees a content CHANGE (so a repeat add of the same product is
    // re-announced) and gives the screen reader a moment to register the region at its new
    // position when it has just been moved into the dialog.
    window.setTimeout(function () {
      region.textContent = message;
    }, 150);
  }

  /* renderContents() runs after cartUpdate is published, so the drawer row for this variant
   * does not exist yet. Poll for it, bounded, and fall back to the response data if the
   * drawer never appears (cart type 'notification' or 'page', or a render failure). */
  function whenRowReady(variantId, item, callback) {
    var started = Date.now();
    (function poll() {
      var row = drawerRowFor(variantId);
      if (row) return callback(row);
      if (Date.now() - started >= POLL_TIMEOUT_MS) return callback(null);
      window.setTimeout(poll, POLL_INTERVAL_MS);
    })();
  }

  /* ---------------------------------------------------------------------------------------
   * TOTAL-ONLY ANNOUNCEMENTS -- drawer opened, quantity changed, item removed.
   *
   * WHAT WAS WRONG. The drawer shows "Estimated total $128.00 USD" and NVDA never said it.
   * Three separate reasons, all verified against the current files rather than assumed:
   *
   *   1. snippets/cart-drawer.liquid marks the totals block <div class="totals" role="status">,
   *      which looks like it should announce. It cannot. CartDrawerItems.getSectionsToRender()
   *      (assets/cart-drawer.js) re-renders with selector '.drawer__inner', and
   *      CartDrawer.renderContents() re-renders '#CartDrawer' -- the totals block is inside
   *      both. A live region that is destroyed and re-created is a new node to the
   *      accessibility API, and screen readers announce changes WITHIN an existing region,
   *      never text that is simply present when one is inserted. Same failure as F14.
   *
   *   2. #CartDrawer-LiveRegionText, the drawer's own status paragraph, is never given text.
   *      Its only writer is cart.js updateLiveRegions(), which sets a message ONLY on a
   *      quantity error and otherwise just toggles aria-hidden on an empty element.
   *
   *   3. This block used to return early for anything but source 'product-form', on the
   *      stated grounds that "cart.js already reports through its own live regions". Reason 2
   *      is why that was wrong: for quantity changes and removals nothing was reported at all.
   *
   * THE FIX. Reuse the one persistent region this block already owns (#AdaCartLiveRegion,
   * which liveRegion() keeps alive across every re-render and moves into the open dialog so
   * aria-modal does not mute it) and write one short sentence into it -- "Estimated total,
   * $128.00 USD" -- on the three moments the total can change or first be seen.
   *
   * No polling: the quantity/removal path rides cart.js's existing
   * publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' }), and the open path watches
   * the class attribute that CartDrawer.open() already sets. assets/cart.js and
   * assets/cart-drawer.js are not modified, so quantity controls, removal, focus handling
   * and the checkout buttons behave exactly as before.
   * ------------------------------------------------------------------------------------ */

  var OPEN_ANNOUNCE_DELAY_MS = 800;
  var lastTotalSentence = '';

  /* "Estimated total, $128.00 USD", or the empty-cart message once the last line goes.
   *
   * itemCount comes from cart.js's parsedState when we have it, because after removing the
   * last item the footer is only hidden by CSS (cart-drawer-items.is-empty + .drawer__footer
   * { display: none }) -- .totals__total-value still exists and still holds a figure, so
   * reading the DOM alone would announce a total for a cart that now reads "empty". */
  function totalSentence(itemCount) {
    var s = strings();
    var drawer = document.querySelector('cart-drawer');
    var isEmpty =
      typeof itemCount === 'number'
        ? itemCount === 0
        : !!(drawer && drawer.classList.contains('is-empty'));

    if (isEmpty) return s.cartEmpty || '';

    var total = cartTotal();
    if (!total) return '';
    return (s.estimatedTotal || 'Estimated total') + ', ' + total;
  }

  /* Announce the total, unless it is the same sentence we last spoke. Re-reading an unchanged
   * figure is the "repeats unnecessarily" case: a clamped quantity, or a second open of a
   * drawer nobody touched, should stay silent. A real change always differs. */
  function announceTotal(itemCount) {
    var sentence = totalSentence(itemCount);
    if (!sentence || sentence === lastTotalSentence) return;
    lastTotalSentence = sentence;
    announce(sentence);
  }

  /* On open, hold briefly and stand down if anything richer was announced in the meantime.
   *
   * Add-to-cart opens the drawer AND publishes cartUpdate, and the two race: renderContents()
   * calls open() from a setTimeout while this block is still polling for the new row. Without
   * this the shopper would hear the bare total first and the useful sentence second. Keying
   * on lastAnnounceAt rather than a timer comparison means the add wins whenever it lands,
   * however long the row takes to appear. */
  var openAnnounceTimer = null;

  function announceTotalOnOpen() {
    var announcedBefore = lastAnnounceAt;
    window.clearTimeout(openAnnounceTimer);
    openAnnounceTimer = window.setTimeout(function () {
      if (lastAnnounceAt !== announcedBefore) return;
      announceTotal();
    }, OPEN_ANNOUNCE_DELAY_MS);
  }

  /* CartDrawer.open() adds 'active' to the <cart-drawer> element and close() removes it.
   * <cart-drawer> itself is never replaced -- renderContents() only rewrites the innerHTML of
   * #CartDrawer, and cart.js only rewrites .drawer__inner -- so one observer on it lasts the
   * life of the page. Watching the class the theme already sets means no new open/close hook
   * and no polling loop. */
  function watchDrawerOpen() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof MutationObserver !== 'function') return;

    var wasActive = drawer.classList.contains('active');
    new MutationObserver(function () {
      var isActive = drawer.classList.contains('active');
      if (isActive && !wasActive) announceTotalOnOpen();
      // Closing clears the memo so re-opening the drawer speaks the total again, which is
      // what a shopper coming back to it expects.
      if (!isActive && wasActive) lastTotalSentence = '';
      wasActive = isActive;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    if (wasActive) announceTotalOnOpen();
  }

  watchDrawerOpen();

  subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
    if (!event) return;

    /* Quantity change and removal. cart.js publishes this AFTER it has re-rendered the
     * sections, so .totals__total-value already holds the new figure -- nothing to wait for.
     * quick-order-list and quick-add-bulk publish 'quick-add' and carry their own aria-live
     * status elements, so they are deliberately not handled here. */
    if (event.source === 'cart-items') {
      var count = event.cartData && typeof event.cartData.item_count === 'number' ? event.cartData.item_count : undefined;
      announceTotal(count);
      return;
    }

    // Add-to-cart: the full sentence, which already ends with the estimated total.
    if (event.source !== 'product-form') return;

    var item = firstItem(event.cartData);
    var variantId = event.productVariantId || (item && item.id) || null;

    whenRowReady(variantId, item, function (row) {
      var sentence = compose(row, item);
      // Keep the memo in step so the total-only paths do not immediately repeat what this
      // sentence just said.
      lastTotalSentence = totalSentence();
      announce(sentence);
    });
  });
})();

/* ===========================================================================
 * Block 6 -- Dialog toggles must report "collapsed" BEFORE focus returns to them.
 *
 * THE BUG (reported as: closing Search still announces "Search dialog opened").
 *
 * assets/details-modal.js closes in this order:
 *
 *     close(focusToggle = true) {
 *       removeTrapFocus(focusToggle ? this.summaryToggle : null);  // 1. focuses the summary
 *       this.detailsContainer.removeAttribute('open');             // 2. THEN closes
 *       ...
 *     }
 *
 * Focus lands on the toggle at step 1, while <details> still carries `open`. The toggle
 * carries aria-haspopup="dialog" and (from Block 1) aria-expanded, so at that instant a
 * screen reader computes the focused control as an EXPANDED dialog toggle and says so --
 * which is heard as "the dialog is still open" right after the user closed it.
 *
 * Block 1 does correct the attribute, but it watches the `open` attribute with a
 * MutationObserver, and those callbacks are microtasks: they run only after the whole click
 * handler has finished. By then the announcement has already been made. The attribute ends up
 * right and the spoken output is wrong.
 *
 * THE FIX. These listeners are registered in the CAPTURE phase on `document`, so they run
 * before any of the theme's own bubble-phase handlers -- including DetailsModal.close(). When
 * a closing gesture is seen on an open dialog, aria-expanded is set to "false" synchronously,
 * so the state is already correct at the moment close() moves focus.
 *
 * WHY IT IS SCOPED TO summary[aria-haspopup="dialog"]. This deliberately does NOT touch plain
 * disclosure menus. The mega menu (Block 2) can keep a <details> open through a click --
 * Enter on a focus-opened menu calls preventDefault() and the menu stays open -- so
 * pre-emptively writing "false" there would leave a stale value that no `open` mutation would
 * ever correct. Dialog toggles have no such case. A requestAnimationFrame re-read of the real
 * state is still done afterwards as a safety net, so if a close is cancelled for any reason
 * the attribute repairs itself on the next frame.
 *
 * Reusable by construction: it keys off the ARIA role of the toggle, not off the search
 * modal, so every <details>-based dialog in the theme is covered by the same code, and any
 * added later is covered automatically. assets/details-modal.js is NOT modified, so focus
 * trapping and focus return are untouched.
 * ======================================================================== */
(function () {
  'use strict';

  var DIALOG_SUMMARY = 'summary[aria-haspopup="dialog"]';
  /* Explicit closing controls inside a dialog: Dawn's modal close button, the click-out
   * overlay, and the drawer close button. */
  var CLOSE_CONTROLS = 'button.modal__close-button, .modal-overlay, .drawer__close';

  function ownSummary(details) {
    for (var i = 0; i < details.children.length; i++) {
      if (details.children[i].tagName === 'SUMMARY') return details.children[i];
    }
    return null;
  }

  function syncFromState(summary, details) {
    if (!summary || !summary.hasAttribute('aria-expanded')) return;
    summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
  }

  function markClosing(details) {
    if (!details || !details.hasAttribute('open')) return;
    var summary = ownSummary(details);
    if (!summary || !summary.matches(DIALOG_SUMMARY)) return;
    if (!summary.hasAttribute('aria-expanded')) return;

    summary.setAttribute('aria-expanded', 'false');

    // Safety net: if the close was cancelled, restore the true state next frame.
    var raf = window.requestAnimationFrame || function (fn) { return window.setTimeout(fn, 16); };
    raf(function () { syncFromState(summary, details); });
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var details = target.closest('details[open]');
      if (!details) return;

      var summary = ownSummary(details);
      var onSummary = summary && (target === summary || summary.contains(target));
      var onCloseControl = !!target.closest(CLOSE_CONTROLS);

      // Clicking the summary of an OPEN dialog closes it; so does a close control. Any other
      // click inside the dialog (typing in the search field, following a result) is ignored.
      if (onSummary || onCloseControl) markClosing(details);
    },
    true
  );

  document.addEventListener(
    'keyup',
    function (event) {
      var key = event.key || event.code;
      if (key !== 'Escape' && key !== 'Esc' && String(key).toUpperCase() !== 'ESCAPE') return;
      var target = event.target;
      if (!target || !target.closest) return;
      markClosing(target.closest('details[open]'));
    },
    true
  );
})();

/* ===========================================================================
 * Block 7 -- Announce that the header search dialog has closed
 *            (WCAG 4.1.3 Status Messages).
 *
 * WHY THIS IS NEEDED ON TOP OF BLOCK 6. Block 6 makes the toggle report
 * "collapsed" at the moment focus returns to it, which is the native signal that the
 * dialog is gone. That is correct but easy to miss: DetailsModal.close() moves focus to
 * the summary, and what a screen reader says there is the control's own name and state
 * ("Search, button, collapsed"), not an event. QA asked for an explicit confirmation, so
 * this adds one -- it does not replace the collapsed state, which stays exactly as it is.
 *
 * WHY A MutationObserver ON THE `open` ATTRIBUTE. The dialog closes through five paths:
 * the close button, Escape, clicking the summary again, an outside click, and
 * DetailsModal.close() called programmatically. Only the attribute is common to all five,
 * and DetailsModal.close() removes it last, so by the time this fires the dialog really is
 * closed and focus has already been returned. A click handler would miss paths and could
 * race close().
 *
 * THE THREE THINGS QA ASKED IT NOT TO DO, and what stops each:
 *
 *   "never on unrelated DOM updates" -- the observer is bound to one element, the search
 *       <details>, with attributeFilter: ['open']. No other mutation reaches it.
 *
 *   "never if the search was never open" -- wasOpen is seeded from the real state when the
 *       observer is wired and the announcement is gated on a true -> false TRANSITION.
 *       A page that loads with the dialog shut, or any mutation that leaves `open` as it
 *       was, announces nothing.
 *
 *   "once, not repeatedly" -- removeAttribute('open') on an already-closed <details> is not
 *       a mutation, so a double close cannot produce a second callback, and wasOpen is
 *       updated before the announcement is queued.
 *
 * LIVE REGION. Reuses the theme's single polite status region (#AdaCartLiveRegion, declared
 * in layout/theme.liquid) rather than adding a second one -- two polite regions on a page
 * invite double or out-of-order announcements. The id is from the add-to-cart work that
 * first needed it; the node itself is a generic role="status" aria-live="polite" region.
 *
 * It is re-homed to <body> before writing. Block 5 moves that node into whichever
 * aria-modal dialog is open so it can be heard under aria-modal, and the search modal is
 * one of those dialogs -- so it can be parked inside a panel that is closing as we write to
 * it, where display:none would swallow the announcement. With no dialog open, <body> is
 * where Block 5 would put it anyway.
 * ======================================================================== */
(function () {
  'use strict';

  var SEARCH_MODAL = 'details-modal.header__search';
  var REGION_ID = 'AdaCartLiveRegion';
  var WIRED_FLAG = 'adaSearchClosedWired';

  /* WHY 600 AND NOT 150 (QA: "Search closed is not actually announced").
   *
   * details-modal.js close() returns focus to the toggle BEFORE it removes the `open`
   * attribute, so the observer below always fires after the focus move. Measured on a
   * reproduction of this header, close-button path:
   *
   *     441ms  focus -> "Close search"
   *     442ms  focus -> "Search"        <- removeTrapFocus returns focus to the toggle
   *     442ms  details.open = false
   *     593ms  live region written      <- only 151ms later
   *
   * At 150ms the update landed while the screen reader was still speaking the focus change
   * ("Search, button, collapsed"). A polite update arriving during or immediately after a
   * focus change is the classic way to lose an announcement: the focus event resets the
   * speech queue, and anything queued in that window can be dropped rather than spoken
   * after. Waiting until the focus utterance has had time to finish is what makes the
   * message survive; polite means it still never interrupts.
   *
   * This is a mitigation, not a guarantee -- speech rate and verbosity settings vary, and
   * none of this has been confirmed with a real screen reader in this environment.
   */
  var ANNOUNCE_DELAY_MS = 600;

  function closedMessage(modal) {
    var override = modal.getAttribute('data-ada-search-closed');
    if (override) return override;
    var strings = window.adaSearchStrings || {};
    return (strings.searchName || 'Search') + ' closed';
  }

  /* The one pending announcement, so a reopen can cancel it. Without this, closing and
   * immediately reopening the search would say "Search closed" while the dialog is open
   * again -- a wrong statement, and worse than saying nothing. */
  var pending = null;

  function cancelPending() {
    if (pending === null) return;
    window.clearTimeout(pending);
    pending = null;
  }

  function announce(message) {
    var region = document.getElementById(REGION_ID);
    if (!region || !message) return;
    // See LIVE REGION note above: never announce from inside a panel that is closing.
    if (region.parentElement !== document.body) document.body.appendChild(region);
    region.textContent = '';
    cancelPending();
    // The clear-then-set guarantees a content change, so closing twice is announced twice.
    pending = window.setTimeout(function () {
      pending = null;
      region.textContent = message;
    }, ANNOUNCE_DELAY_MS);
  }

  function wire(modal) {
    if (!modal || modal.dataset[WIRED_FLAG] === 'true') return;
    if (typeof MutationObserver !== 'function') return;

    var details = modal.querySelector('details');
    if (!details) return;

    var wasOpen = details.hasAttribute('open');

    new MutationObserver(function () {
      var isOpen = details.hasAttribute('open');
      if (wasOpen === isOpen) return;
      wasOpen = isOpen;
      // Reopening within the delay window retracts the pending "closed" message.
      if (isOpen) cancelPending();
      else announce(closedMessage(modal));
    }).observe(details, { attributes: true, attributeFilter: ['open'] });

    modal.dataset[WIRED_FLAG] = 'true';
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SEARCH_MODAL).forEach(wire);
  }

  init(document);

  // The header can be re-rendered in the theme editor, which replaces this markup.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
